// Owned by AIOE. Mirrored by the workbench (shared/schemas/rbac.ts) — keep the two identical.
/**
 * Roles and permissions (docs/rbac.md, 0.90.0 item 4). Seven verbs mean the same on every kind of
 * resource; a role is a set of verbs on each kind (or one area of a kind); a role assignment gives a
 * role to a person, a directory group or an agent at a scope: the organisation, one team or one
 * project. The API decides every request (`packages/api/src/auth/rbac.ts` `can()`); the console asks
 * `GET /me/v1/permissions` what the person may do and hides the rest.
 *
 *   GET    /me/v1/permissions                  what the caller may do, by kind, area and scope
 *   GET    /admin/v1/rbac                      the Roles and permissions screen: roles, assignments, counts, badges
 *   POST   /admin/v1/rbac/roles                a custom role                       (recent sign-in; never beyond the caller's own)
 *   PUT    /admin/v1/rbac/roles/:id            change a custom role                 (same)
 *   DELETE /admin/v1/rbac/roles/:id            archive a custom role                (same; its assignments go with it)
 *   POST   /admin/v1/rbac/assignments          give a role to a person, group or agent at a scope (same)
 *   DELETE /admin/v1/rbac/assignments/:id      take it away                         (same)
 *
 * With AIOE_RBAC off (the default until 0.90.0 turns it on) the screen shows today's model read-only:
 * what each of today's roles may do, computed by the same `can()` from today's rules.
 */
import { z } from "zod";
export const RBAC_SCHEMA_VERSION = "aioe.rbac/v1";
/* ---------- the seven verbs ---------- */
export const VERBS = ["view", "read", "create", "update", "delete", "act", "manage"];
export const verbSchema = z.enum(VERBS);
/** Each verb in words, exactly as docs/rbac.md section 2 defines it. A verb does not include the ones before it. */
export const VERB_TEXT = {
    view: { label: "View", letter: "V", means: "Know it exists: see it in a list or menu with its name, state and counts, but not what is inside." },
    read: { label: "Read", letter: "R", means: "Open it and see its contents and history. Data classification still applies, and a secret's value is never read by a person." },
    create: { label: "Create", letter: "C", means: "Make a new one, or submit one for approval." },
    update: { label: "Update", letter: "U", means: "Change one that exists, including publishing a new version of it." },
    delete: { label: "Delete", letter: "D", means: "Remove it or archive it. Removal that cannot be undone needs a recent sign-in." },
    act: { label: "Act", letter: "A", means: "Make it do something, or decide on something, without changing what it is: run, approve or trigger." },
    manage: { label: "Manage", letter: "M", means: "Decide who may do what with it: assign roles on it, change its owner, set its readers. Nobody grants more than they hold." },
};
/* ---------- resources ---------- */
/** Every console area belongs to one kind (docs/rbac.md section 3); a role is ticked per kind, or per area of a kind. */
export const RESOURCE_AREAS = {
    agents: ["specialists", "activity", "live", "identities"],
    teams: ["teams", "portfolio"],
    projects: ["projects", "register"],
    workflows: ["workflows", "routines", "apps", "event-sources"],
    machines: ["workbenches", "nodes", "mesh", "mesh-settings", "orders", "updates", "backups"],
    rules: ["policies", "exceptions", "providers", "tools", "catalogue", "customisations", "design-systems"],
    data: ["memory", "secrets", "quotas"],
    economics: ["costs", "budgets", "billing", "pricing", "chargeback", "invoices"],
    roles: ["roles", "people", "sign-in"],
    audit: ["audit", "forwarding", "settings", "platform-access"],
    organisation: ["settings", "integrations", "export", "keys", "licence", "status", "readiness", "platform"],
};
export const RESOURCE_KINDS = Object.keys(RESOURCE_AREAS);
export const resourceKindSchema = z.enum(RESOURCE_KINDS);
export const AREA_KEYS = RESOURCE_KINDS.flatMap((k) => RESOURCE_AREAS[k].map((a) => `${k}.${a}`));
const AREA_SET = new Set(AREA_KEYS);
export const isAreaKey = (s) => AREA_SET.has(s);
export const kindOf = (area) => area.slice(0, area.indexOf("."));
/** Each kind in words, with the console areas it covers (the screen's editor and the narrow cards). */
export const KIND_TEXT = {
    agents: { label: "Agents", covers: "Specialists and agent identities, Activity and Live" },
    teams: { label: "Teams", covers: "Teams, the portfolio and its business areas" },
    projects: { label: "Projects", covers: "Projects and everything inside one" },
    workflows: { label: "Workflows", covers: "Workflows, routines, apps and event sources" },
    machines: { label: "Machines", covers: "Workbenches, nodes, the mesh, updates and backups" },
    rules: { label: "Rules", covers: "Policies and exceptions, providers and models, tools, the catalogue, customisations and design systems" },
    data: { label: "Data", covers: "Memory, secrets and quotas" },
    economics: { label: "Economics", covers: "Costs, budgets, pricing, chargeback and billing" },
    roles: { label: "Roles", covers: "Roles and permissions, people and single sign-on" },
    audit: { label: "Audit", covers: "The audit trail, where it is sent, and platform access" },
    organisation: { label: "Organisation", covers: "The organisation's settings, integrations, export, keys, licence and status" },
};
/** Each area in words. */
export const AREA_TEXT = {
    "agents.specialists": "Specialists",
    "agents.activity": "Activity",
    "agents.live": "Live sessions",
    "agents.identities": "Agent identities",
    "teams.teams": "Teams",
    "teams.portfolio": "Portfolio and business areas",
    "projects.projects": "Projects",
    "projects.register": "The organisation's project register",
    "workflows.workflows": "Workflows",
    "workflows.routines": "Routines",
    "workflows.apps": "Apps",
    "workflows.event-sources": "Event sources",
    "machines.workbenches": "Workbenches",
    "machines.nodes": "Nodes",
    "machines.mesh": "Mesh nodes and flows",
    "machines.mesh-settings": "Mesh policy, relays and webhooks",
    "machines.orders": "Orders to machines",
    "machines.updates": "Updates and releases",
    "machines.backups": "Backups and recovery",
    "rules.policies": "Policies",
    "rules.exceptions": "Policy exceptions",
    "rules.providers": "Providers, models and subscriptions",
    "rules.tools": "Tools",
    "rules.catalogue": "Catalogue",
    "rules.customisations": "Customisations",
    "rules.design-systems": "Design systems",
    "data.memory": "Memory",
    "data.secrets": "Secrets",
    "data.quotas": "Quotas and storage",
    "economics.costs": "Costs",
    "economics.budgets": "Budgets",
    "economics.billing": "Billing",
    "economics.pricing": "Pricing",
    "economics.chargeback": "Chargeback statements",
    "economics.invoices": "Invoices and chargeback sends",
    "roles.roles": "Roles and permissions",
    "roles.people": "People",
    "roles.sign-in": "Single sign-on and provisioning",
    "audit.audit": "Audit trail",
    "audit.forwarding": "Audit and run forwarding",
    "audit.settings": "Audit settings",
    "audit.platform-access": "Platform access",
    "organisation.settings": "Organisation settings",
    "organisation.integrations": "Integrations",
    "organisation.export": "Export",
    "organisation.keys": "Encryption keys",
    "organisation.licence": "Licence",
    "organisation.status": "Status page",
    "organisation.readiness": "Readiness report",
    "organisation.platform": "This platform",
};
const VERB_SET = new Set(VERBS);
const KIND_SET = new Set(RESOURCE_KINDS);
export function parseGrant(s) {
    const m = /^([a-z]+)(?:\.([a-z-]+))?:([a-z]+)(@own)?$/.exec(s);
    if (!m || !KIND_SET.has(m[1]) || !VERB_SET.has(m[3]))
        return undefined;
    const kind = m[1];
    if (m[2] !== undefined && !AREA_SET.has(`${kind}.${m[2]}`))
        return undefined;
    return { kind, ...(m[2] !== undefined ? { area: `${kind}.${m[2]}` } : {}), verb: m[3], own: !!m[4] };
}
export const grantSchema = z.custom((s) => typeof s === "string" && s.length <= 64 && parseGrant(s) !== undefined, { message: "a grant is kind:verb or kind.area:verb, with @own for the holder's own things" });
/** Whether `grants` allow `verb` on `area`; `own` when the resource is the holder's own (an `@own` grant counts then). */
export function grantsAllow(grants, area, verb, own = false) {
    const kind = kindOf(area);
    for (const s of grants) {
        const g = parseGrant(s);
        if (!g || g.verb !== verb || g.kind !== kind)
            continue;
        if (g.area && g.area !== area)
            continue;
        if (g.own && !own)
            continue;
        return true;
    }
    return false;
}
/** Every area's verbs a set of grants holds: `all` on every thing in the area, `own` only on the holder's own. */
export function grantTable(grants) {
    const list = [...grants];
    const out = {};
    for (const area of AREA_KEYS) {
        const all = VERBS.filter((v) => grantsAllow(list, area, v, false));
        const own = VERBS.filter((v) => !all.includes(v) && grantsAllow(list, area, v, true));
        out[area] = { all, own };
    }
    return out;
}
/** The smallest grant list that holds the same as `grants`: whole kinds where every area holds a verb, areas otherwise. */
export function compactGrants(grants) {
    const table = grantTable(grants);
    const out = [];
    for (const kind of RESOURCE_KINDS) {
        const areas = RESOURCE_AREAS[kind].map((a) => `${kind}.${a}`);
        for (const verb of VERBS) {
            if (areas.every((a) => table[a].all.includes(verb)))
                out.push(`${kind}:${verb}`);
            else if (areas.every((a) => table[a].all.includes(verb) || table[a].own.includes(verb))) {
                for (const a of areas)
                    if (table[a].all.includes(verb))
                        out.push(`${a}:${verb}`);
                out.push(`${kind}:${verb}@own`);
            }
            else
                for (const a of areas) {
                    if (table[a].all.includes(verb))
                        out.push(`${a}:${verb}`);
                    else if (table[a].own.includes(verb))
                        out.push(`${a}:${verb}@own`);
                }
        }
    }
    return out;
}
/** Grants no person ever exercises (a secret's value is released to machines, never read): never counted as beyond anyone. */
export const NEVER_HELD = ["data.secrets:read"];
/** Whether every grant in `wanted` is held by `held` (no escalation: nobody grants more than they hold). */
export function grantsWithin(wanted, held) {
    const want = grantTable(wanted);
    const have = grantTable([...held, ...NEVER_HELD]);
    const beyond = AREA_KEYS.filter((a) => want[a].all.some((v) => !have[a].all.includes(v)) || want[a].own.some((v) => !have[a].all.includes(v) && !have[a].own.includes(v)));
    return beyond.length ? { ok: false, beyond } : { ok: true };
}
export function verbReach(grants) {
    const table = grantTable(grants);
    const out = {};
    for (const v of VERBS) {
        // What nobody ever holds (a secret's value) does not make a role's Read partial.
        const areas = AREA_KEYS.filter((a) => !NEVER_HELD.includes(`${a}:${v}`));
        const full = areas.filter((a) => table[a].all.includes(v)).length;
        const any = areas.filter((a) => table[a].all.includes(v) || table[a].own.includes(v)).length;
        out[v] = full === areas.length ? "all" : any ? "some" : "none";
    }
    return out;
}
/**
 * Builds a grant list from letters (docs/rbac.md's matrix): `{ "*": "VR", "data.secrets": "V|CUDA" }` is View and Read
 * on everything, then on secrets View, and Create, Update, Delete and Act on one's own. Later keys add to earlier ones.
 */
export function lettersToGrants(spec) {
    const byLetter = new Map(VERBS.map((v) => [VERB_TEXT[v].letter, v]));
    const out = new Set();
    for (const [key, letters] of Object.entries(spec)) {
        const [plain = "", own = ""] = letters.split("|");
        const targets = key === "*" ? RESOURCE_KINDS : [key];
        for (const t of targets) {
            for (const c of plain)
                out.add(`${t}:${byLetter.get(c)}`);
            for (const c of own)
                out.add(`${t}:${byLetter.get(c)}@own`);
        }
    }
    for (const g of out)
        if (!parseGrant(g))
            throw new Error(`not a grant: ${g}`);
    return [...out];
}
/* ---------- roles ---------- */
export const SCOPE_TYPES = ["organisation", "team", "project"];
export const scopeTypeSchema = z.enum(SCOPE_TYPES);
export const PRINCIPAL_TYPES = ["user", "group", "agent"];
export const principalTypeSchema = z.enum(PRINCIPAL_TYPES);
export const PRINCIPAL_TYPE_TEXT = { user: "Person", group: "Group", agent: "Agent" };
export const BUILT_IN_ROLE_IDS = ["platform-administrator", "team-administrator", "member", "viewer"];
export const isBuiltInRole = (id) => BUILT_IN_ROLE_IDS.includes(id);
/**
 * The four built-in roles, cannot be edited (docs/rbac.md section 4, JP's screen). Their grants follow the matrix by
 * console area; Team Administrator holds everything but only on the team's or project's own resources (it is assigned
 * at a team or a project, never the organisation).
 */
export const BUILT_IN_ROLES = {
    "platform-administrator": {
        name: "Platform Administrator",
        description: "Everything in this organisation. Never another organisation's.",
        scopes: ["organisation"],
        grants: lettersToGrants({ "*": "VRCUDAM" }),
    },
    "team-administrator": {
        name: "Team Administrator",
        description: "Everything on one team's or one project's own resources.",
        scopes: ["team", "project"],
        grants: lettersToGrants({ "*": "VRCUDAM" }),
    },
    member: {
        name: "Member",
        description: "Uses and contributes: views, reads, creates, changes and runs; deletes only their own.",
        scopes: ["organisation", "team", "project"],
        grants: lettersToGrants({
            "agents.specialists": "VRCUA|D",
            "agents.activity": "VR|A",
            "agents.live": "VR|A",
            "agents.identities": "V",
            "teams.teams": "VRCUA|D",
            "teams.portfolio": "VR",
            "projects.projects": "VRCUA|D",
            "projects.register": "VC",
            workflows: "VRCUA",
            "machines.workbenches": "|VRCUDA",
            "machines.nodes": "|VR",
            "machines.mesh": "|VR",
            "machines.backups": "|VRCUD",
            "rules.policies": "VR",
            "rules.exceptions": "VRC",
            "rules.providers": "VR",
            "rules.tools": "VR",
            "rules.catalogue": "VRC",
            "rules.customisations": "VRC",
            "rules.design-systems": "VR",
            "data.memory": "VRCUA",
            "data.secrets": "V|CUDA",
            "data.quotas": "|VR",
            "economics.costs": "|VR",
            "economics.budgets": "VR",
            "organisation.settings": "VR",
        }),
    },
    viewer: {
        name: "Viewer",
        description: "Sees and reads; changes nothing.",
        scopes: ["organisation", "team", "project"],
        grants: lettersToGrants({
            agents: "VR",
            "agents.identities": "V",
            teams: "VR",
            projects: "VR",
            workflows: "VR",
            "machines.workbenches": "|VR",
            "machines.nodes": "V",
            rules: "VR",
            "data.memory": "VR",
            "data.secrets": "V",
            "data.quotas": "V",
            "organisation.settings": "VR",
        }),
    },
};
/**
 * What the `operator` app role reaches today, by role alone (decision 3: an editable template). The mesh and fleet
 * across every machine, every node and its assignments, the organisation's activity and live sessions, the audit trail,
 * tools and customisations. What `PUT /admin/v1/roles` ticked for `operator` comes on top, read through the aliases.
 */
export const OPERATOR_GRANTS = lettersToGrants({
    "machines.workbenches": "VRA",
    "machines.nodes": "VR",
    "machines.mesh": "VRU",
    "agents.activity": "VR",
    "agents.live": "VR",
    "audit.audit": "VR",
    "rules.tools": "VR",
    "rules.customisations": "VR",
});
/**
 * What the `portfolio-manager` role (or `portfolio:manage`) reaches today: every team and project (an owner of each),
 * every business area, the organisation's activity, its costs and adoption, and chargeback statements. Not the
 * administrators' project register (`/admin/v1/projects`), which stays an administrator's today.
 */
export const PORTFOLIO_MANAGER_GRANTS = lettersToGrants({
    teams: "VRCUDAM",
    "projects.projects": "VRCUDAM",
    "agents.activity": "VR",
    "economics.costs": "VR",
    "economics.chargeback": "VR",
});
export const ROLE_TEMPLATE_IDS = ["security-analyst", "auditor", "billing", "operator", "portfolio-manager"];
/**
 * Templates seeded into each organisation as custom roles, so they can be edited (decision 3). Seeded once: archiving
 * one keeps it archived.
 */
export const ROLE_TEMPLATES = {
    "security-analyst": {
        name: "Security Analyst",
        description: "Views, reads and acts on everything: approves and declines held actions and exceptions; creates and changes nothing.",
        scopes: ["organisation", "team"],
        grants: lettersToGrants({ "*": "VRA" }),
    },
    auditor: { name: "Auditor", description: "Views and reads everything, the audit trail and platform access included.", scopes: ["organisation", "team"], grants: lettersToGrants({ "*": "VR" }) },
    billing: { name: "Billing", description: "Everything on economics; views elsewhere.", scopes: ["organisation"], grants: lettersToGrants({ "*": "V", economics: "VRCUDAM" }) },
    operator: { name: "Operator", description: "What the operator role reaches today: the fleet, nodes and mesh, activity, the audit trail, tools and customisations.", scopes: ["organisation"], grants: OPERATOR_GRANTS },
    "portfolio-manager": { name: "Portfolio manager", description: "Everything on teams and projects, with the organisation's activity and costs.", scopes: ["organisation"], grants: PORTFOLIO_MANAGER_GRANTS },
};
/* ---------- where today's roles map (step 4) ---------- */
/**
 * Entra app role values (the token's `roles`) and the organisation role each gives (decision 9: app roles, assigned to
 * people or groups in Entra, are the source for organisation-wide roles). `admin` and `operator` exist today; the others
 * are new values an organisation may add to its app registration.
 */
export const APP_ROLE_ROLES = {
    admin: "platform-administrator",
    operator: "operator",
    "portfolio-manager": "portfolio-manager",
    member: "member",
    viewer: "viewer",
    auditor: "auditor",
    "security-analyst": "security-analyst",
    billing: "billing",
};
/** AI Cloud membership roles and the built-in each is: owner is a Platform Administrator marked owner (decision 2). */
export const CLOUD_ROLE_ROLES = {
    owner: "platform-administrator",
    admin: "platform-administrator",
    member: "member",
    viewer: "viewer",
};
/** Project roles keep their wire names (projects.ts is mirrored by the workbench) and are these project-scoped built-ins. */
export const PROJECT_ROLE_ROLES = {
    owner: "team-administrator",
    contributor: "member",
    viewer: "viewer",
};
/** The role a signed-in person holds across the organisation when nothing else names one. */
export const DEFAULT_ORGANISATION_ROLE = "member";
/** The most an agent may hold at any scope (decision 4): never Manage, never anything on Roles. */
export const AGENT_CEILING = "member";
/** Custom roles an organisation may have, by edition and plan (decision 5). */
export const CUSTOM_ROLE_LIMITS = { enterprise: Number.POSITIVE_INFINITY, cloudTeams: 5, cloudFree: 0 };
/**
 * Roles that, held across the organisation, stay at the organisation: they never reach inside a team or a project by
 * themselves (a member reads a project only as one of its members, as today). Every other organisation-scope role
 * (Platform Administrator, the templates, custom roles) reaches everything in the organisation it grants.
 */
export const STAYS_AT_ITS_SCOPE = ["member", "viewer"];
/**
 * Today's three permissions (`PUT /admin/v1/roles`) as grants (decision 10: the old route stays as an alias until 1.0).
 * `nodes:approve` sees every node and approves; `portfolio:manage` is the Portfolio manager's reach; `memory:write` writes
 * the organisation's memory.
 */
export const LEGACY_PERMISSION_GRANTS = {
    "nodes:approve": ["machines.nodes:view", "machines.nodes:read", "machines.nodes:act"],
    "portfolio:manage": PORTFOLIO_MANAGER_GRANTS,
    "memory:write": ["data.memory:create", "data.memory:update"],
};
/**
 * What only an owner may do on AI Cloud, where an admin is not an owner (decision 2 and 6): change billing, export the
 * organisation, send its audit trail or runs elsewhere, and change who the owners are.
 */
export const OWNER_ONLY = [
    "economics.billing:create",
    "economics.billing:update",
    "economics.billing:delete",
    "economics.billing:manage",
    "organisation.export:view",
    "organisation.export:read",
    "organisation.export:create",
    "organisation.export:act",
    "organisation.export:delete",
    "audit.forwarding:update",
    "audit.forwarding:act",
    "roles.people:manage",
];
/* ---------- the wire ---------- */
/** `today`: one of today's roles, shown read-only while AIOE_RBAC is off (what it reaches now, computed by `can()`). */
export const roleSourceSchema = z.enum(["built-in", "template", "custom", "today"]);
export const roleDefinitionSchema = z.object({
    id: z.string().min(1).max(80),
    name: z.string().min(1).max(80),
    description: z.string().max(400).optional(),
    source: roleSourceSchema,
    /** The template it was seeded from. */
    template: z.enum(ROLE_TEMPLATE_IDS).optional(),
    scopes: z.array(scopeTypeSchema).min(1),
    grants: z.array(grantSchema).max(800),
    updatedAt: z.string().optional(),
    updatedBy: z.string().optional(),
});
/** A custom role as written: a name, words, where it may be assigned, and its grants. */
export const roleDefinitionBodySchema = z.object({
    name: z.string().trim().min(1).max(80),
    description: z.string().trim().max(400).optional(),
    scopes: z.array(scopeTypeSchema).min(1).max(3).optional(),
    grants: z.array(grantSchema).max(800),
});
export const roleScopeSchema = z.discriminatedUnion("type", [
    z.object({ type: z.literal("organisation") }),
    z.object({ type: z.literal("team"), id: z.string().min(1).max(200), name: z.string().max(200).optional() }),
    z.object({ type: z.literal("project"), id: z.string().min(1).max(200), name: z.string().max(200).optional() }),
]);
export const rolePrincipalSchema = z.object({
    type: principalTypeSchema,
    /** A person's id, a directory group's id or name, or an agent `agent:<teamId>/<memberName>`. */
    id: z.string().min(1).max(300),
    name: z.string().max(200).optional(),
});
/** Where a holder's role comes from: an assignment on this screen, or today's sources read through `can()`. */
export const assignmentSourceSchema = z.enum(["assigned", "app-role", "cloud-role", "project", "team-owner", "area-manager", "default"]);
export const roleAssignmentSchema = z.object({
    id: z.string(),
    principal: rolePrincipalSchema,
    roleId: z.string(),
    scope: roleScopeSchema,
    source: assignmentSourceSchema,
    createdAt: z.string().optional(),
    createdBy: z.string().optional(),
});
export const roleAssignmentBodySchema = z.object({ principal: rolePrincipalSchema, roleId: z.string().min(1).max(80), scope: roleScopeSchema });
/** One verb set per area, for one scope: `all` on everything there, `own` on the caller's own things only. */
export const areaVerbsSchema = z.record(z.string(), z.object({ all: z.array(verbSchema), own: z.array(verbSchema) }));
/** `GET /me/v1/permissions`: the console's single source for what to show. */
export const myPermissionsSchema = z.object({
    schema: z.literal(RBAC_SCHEMA_VERSION),
    /** `today`: AIOE_RBAC off, today's rules; `roles`: role definitions and assignments decide. */
    mode: z.enum(["today", "roles"]),
    edition: z.enum(["enterprise", "cloud"]),
    /** An owner of the organisation (decision 2): billing, export, deletion. */
    owner: z.boolean(),
    /** Across the organisation, by area. */
    organisation: areaVerbsSchema,
    /** By kind, the verbs held on at least one area (what a menu needs). */
    kinds: z.record(z.string(), z.array(verbSchema)),
    /** Inside one team or project, beyond the organisation's: the role ids that apply there. */
    scoped: z.array(z.object({ scope: roleScopeSchema, roleIds: z.array(z.string()) })),
    /** The roles held, in words, for "your access". */
    held: z.array(z.object({ roleId: z.string(), name: z.string(), scope: roleScopeSchema, source: assignmentSourceSchema })),
    /** The grants of every role named in `scoped` and `held`. */
    roles: z.record(z.string(), z.array(grantSchema)),
});
/** A badge on the screen, shown only when it is true for this deployment (docs/rbac.md section 8). */
export const rbacBadgeSchema = z.object({ key: z.enum(["agent-identity", "secure-runtimes", "policy-engine", "audit-trail"]), label: z.string(), detail: z.string() });
/** `GET /admin/v1/rbac`. */
export const rbacOverviewSchema = z.object({
    schema: z.literal(RBAC_SCHEMA_VERSION),
    mode: z.enum(["today", "roles"]),
    edition: z.enum(["enterprise", "cloud"]),
    /** Whether roles and assignments can be changed here: roles on, the caller may manage roles, and the plan allows it. */
    editable: z.boolean(),
    /** Why not, in words, when not. */
    readOnlyReason: z.string().optional(),
    customRoleLimit: z.number().nullable(),
    roles: z.array(roleDefinitionSchema),
    assignments: z.array(roleAssignmentSchema),
    /** `waiting`: held actions for a person to approve (agents' high-impact actions, step 6; 0 until then). */
    counts: z.object({ people: z.number(), agents: z.number(), groups: z.number(), platformAdministrators: z.number(), waiting: z.number() }),
    /** Where a role can be given, and to which agents: the organisation's teams, projects and specialists. */
    teams: z.array(z.object({ id: z.string(), name: z.string() })),
    projects: z.array(z.object({ id: z.string(), name: z.string() })),
    agents: z.array(z.object({ id: z.string(), name: z.string(), team: z.string() })),
    badges: z.array(rbacBadgeSchema),
    /** The caller's own grants across the organisation: an editor never ticks beyond them. */
    mine: z.array(grantSchema),
});
//# sourceMappingURL=rbac.js.map